(() => {
  if (customElements.get('product-quantity-buttons')) return;

  class ProductQuantityButtons extends HTMLElement {
    constructor() {
      super();
      this.changeEvent = new Event('change', { bubbles: true });
      this.inputEvent = new Event('input', { bubbles: true });
      this.onChange = this.onChange.bind(this);
      this.onQuantityChange = this.onQuantityChange.bind(this);
      this.onQuantityRulesUpdate = this.onQuantityRulesUpdate.bind(this);
      this.onSellingPlanChange = this.onSellingPlanChange.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onOneTimePurchase = this.onOneTimePurchase.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.syncState = this.syncState.bind(this);
      this.selectedQuantity = null;
      this.defaultFrequencyQuantity = null;
      this.selectedSellingPlanId = null;
      this.selectedOfferHandle = null;
      this.reapplyTimers = [];
      this.sellingPlanReapplyTimers = [];
      this.initialSellingPlanSyncTimers = [];
      this.formSubmitTarget = null;
    }

    connectedCallback() {
      this.addEventListener('change', this.onChange);
      document.addEventListener('input', this.onQuantityChange);
      document.addEventListener('change', this.onQuantityChange);
      document.addEventListener('skio::update-selling-plan', this.onSellingPlanChange);
      this.attachFormSubmitListener();
      this.oneTimePurchaseButton = this.getOneTimePurchaseButton();
      this.oneTimePurchaseButton?.addEventListener('click', this.onOneTimePurchase);
      window.setTimeout(() => this.attachFormSubmitListener(), 0);
      this.subscribeToQuantityUpdates();
      this.subscribeToVariantChanges();
      this.syncManagedInputState();
      this.syncBadgeVisibility();
      this.applyInitialQuantity();
      this.scheduleInitialSellingPlanSync();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('input', this.onQuantityChange);
      document.removeEventListener('change', this.onQuantityChange);
      document.removeEventListener('skio::update-selling-plan', this.onSellingPlanChange);
      this.detachFormSubmitListener();
      this.oneTimePurchaseButton?.removeEventListener('click', this.onOneTimePurchase);

      if (this.quantityUpdateUnsubscriber) {
        this.quantityUpdateUnsubscriber();
      }

      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }

      this.clearScheduledApply();
      this.clearScheduledSellingPlanApply();
      this.clearInitialSellingPlanSync();
    }

    applyInitialQuantity() {
      const quantity = this.getCheckedQuantity() || this.getDefaultQuantity();
      const quantityInput = this.getQuantityInput();

      if (!quantity || !quantityInput) {
        this.syncState();
        return;
      }

      if (!this.isAllowedQuantity(quantity, quantityInput)) {
        this.syncState();
        return;
      }

      this.selectedQuantity = quantity;
      this.selectedOfferHandle = this.getCheckedOfferHandle();
      this.setQuantityValue(quantity);

      const checkedInput = Array.from(this.querySelectorAll('[data-quantity-button-input]')).find((input) => {
        return parseInt(input.value, 10) === quantity;
      });
      if (checkedInput && checkedInput.checked && !checkedInput.disabled) {
        this.syncSellingPlanForQuantity(checkedInput);
      }

      this.syncState();
    }

    scheduleInitialSellingPlanSync() {
      this.clearInitialSellingPlanSync();

      [0, 80, 180, 350, 700, 1200].forEach((delay) => {
        const timer = window.setTimeout(() => {
          this.applyInitialSellingPlanSync();
        }, delay);

        this.initialSellingPlanSyncTimers.push(timer);
      });
    }

    clearInitialSellingPlanSync() {
      this.initialSellingPlanSyncTimers.forEach((timer) => window.clearTimeout(timer));
      this.initialSellingPlanSyncTimers = [];
    }

    applyInitialSellingPlanSync() {
      const quantity = this.getCheckedQuantity() || this.getDefaultQuantity();
      if (!quantity) return;

      if (this.getCurrentQuantity() !== quantity) {
        this.setQuantityValue(quantity);
      }

      const checkedInput = Array.from(this.querySelectorAll('[data-quantity-button-input]')).find((input) => {
        return parseInt(input.value, 10) === quantity;
      });

      if (!checkedInput || checkedInput.disabled) return;

      checkedInput.checked = true;
      this.syncSellingPlanForQuantity(checkedInput);
      this.syncState();
    }

    onChange(event) {
      const input = event.target.closest('[data-quantity-button-input]');
      if (!input || !input.checked || input.disabled) return;

      const quantity = parseInt(input.value, 10);
      const quantityInput = this.getQuantityInput();
      if (!quantityInput || !this.isAllowedQuantity(quantity, quantityInput)) {
        this.syncState();
        return;
      }

      this.selectedQuantity = quantity;
      this.setQuantityValue(quantity);
      this.syncSellingPlanForQuantity(input);
      this.syncState();
    }

    onQuantityChange(event) {
      if (this.getQuantityInputs().includes(event.target)) {
        const quantity = parseInt(event.target.value, 10);
        if (this.isAllowedQuantity(quantity, event.target)) {
          this.selectedQuantity = quantity;
          this.setQuantityValue(quantity);
        }

        this.syncState();
      }

      if (event.target === this.getSellingPlanInput()) {
        this.syncBadgeVisibility();
        this.scheduleApplySelectedQuantity();
      }
    }

    onQuantityRulesUpdate() {
      this.scheduleApplySelectedQuantity();
    }

    onVariantChange(event) {
      const sectionId = event.data?.sectionId;
      if (sectionId && String(sectionId) !== String(this.dataset.section)) return;

      this.updateOneTimePurchasePrice();
    }

    onFormSubmit() {
      const skio = this.getSkioPlanPicker();
      const currentSellingPlanId = this.selectedSellingPlanId;
      const submitSellingPlanId = this.hasActiveSellingPlan(skio)
        ? currentSellingPlanId && this.isSellingPlanAvailableForCurrentVariant(currentSellingPlanId)
          ? currentSellingPlanId
          : this.getSellingPlanIdForQuantity(this.getCheckedQuantity() || this.getCurrentQuantity(), this.getCurrentSellingPlanFrequency(skio))
        : null;

      if (submitSellingPlanId) {
        this.selectedSellingPlanId = submitSellingPlanId;
        this.applySelectedSellingPlan();
      }

      if (!this.selectedSellingPlanId || !this.isSellingPlanAvailableForCurrentVariant(this.selectedSellingPlanId)) return;

      this.updateSellingPlanInputs(this.selectedSellingPlanId);
    }

    async onOneTimePurchase(event) {
      event.preventDefault();

      const button = event.currentTarget;
      const variantId = this.getVariantInput()?.value;
      const quantity = this.getCheckedQuantity() || this.getCurrentQuantity() || this.getDefaultQuantity();
      const variant = this.getVariantData().find((item) => String(item.id) === String(variantId));

      if (!variantId || !quantity || !variant?.available || button.disabled) return;

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('loading');
      button.querySelector('.loading__spinner')?.classList.remove('hidden');

      try {
        const response = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });
        const cartData = await response.json();

        if (!response.ok || cartData.status) throw new Error(cartData.description || 'Unable to add this product to cart.');

        window.location.assign('/checkout');
      } catch (error) {
        console.error(error);
        this.dispatchConfigurationError(error.message);
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.classList.remove('loading');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
      }
    }

    attachFormSubmitListener() {
      const form = this.getForm();
      if (!form || form === this.formSubmitTarget) return;

      this.detachFormSubmitListener();
      form.addEventListener('submit', this.onFormSubmit, true);
      this.formSubmitTarget = form;
    }

    detachFormSubmitListener() {
      if (!this.formSubmitTarget) return;

      this.formSubmitTarget.removeEventListener('submit', this.onFormSubmit, true);
      this.formSubmitTarget = null;
    }

    onSellingPlanChange(event) {
      if (!this.isRelevantSellingPlanEvent(event)) return;

      const sellingPlanId = event.detail?.sellingPlan?.id ? String(event.detail.sellingPlan.id) : null;
      if (sellingPlanId) {
        // Skio is the source of truth when the customer changes delivery days.
        // Stop delayed plan restores from initial setup; they could otherwise
        // overwrite this selection with the 30-day plan.
        this.selectedSellingPlanId = sellingPlanId;
        this.clearScheduledSellingPlanApply();
      } else if (!sellingPlanId) {
        this.selectedSellingPlanId = null;
        this.clearScheduledSellingPlanApply();
      }

      this.syncBadgeVisibility(event.detail?.sellingPlan);
      this.scheduleApplySelectedQuantity();
    }

    subscribeToQuantityUpdates() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.onQuantityRulesUpdate);
    }

    subscribeToVariantChanges() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
    }

    getQuantityInput() {
      return this.getQuantityInputs()[0] || null;
    }

    collectQuantityInputs() {
      const inputs = [];
      const form = document.getElementById(this.dataset.formId);

      if (form) {
        inputs.push(...form.querySelectorAll('input[name="quantity"]'));
      }

      if (window.CSS?.escape && this.dataset.formId) {
        inputs.push(...document.querySelectorAll(`input[name="quantity"][form="${CSS.escape(this.dataset.formId)}"]`));
      }

      const sectionInput = document.getElementById(`Quantity-${this.dataset.section}`);
      if (sectionInput) inputs.push(sectionInput);

      return [...new Set(inputs)];
    }

    getQuantityInputs() {
      this.syncManagedInputState();
      return this.collectQuantityInputs().filter((input) => !input.disabled);
    }

    getManagedQuantityInput() {
      return this.querySelector('[data-quantity-buttons-form-input]');
    }

    syncManagedInputState() {
      const managedInput = this.getManagedQuantityInput();
      if (!managedInput) return;

      const hasNativeQuantityInput = this.collectQuantityInputs().some((input) => input !== managedInput && !input.disabled);
      managedInput.disabled = hasNativeQuantityInput;
    }

    getSellingPlanInput() {
      const form = document.getElementById(this.dataset.formId);
      const formInput = form ? form.querySelector('input[name="selling_plan"]') : null;
      if (formInput) return formInput;

      if (window.CSS?.escape && this.dataset.formId) {
        const externalFormInput = document.querySelector(`input[name="selling_plan"][form="${CSS.escape(this.dataset.formId)}"]`);
        if (externalFormInput) return externalFormInput;
      }

      return document.querySelector('input[name="selling_plan"]');
    }

    syncBadgeVisibility(sellingPlan = undefined) {
      this.classList.remove('product-quantity-buttons--hide-badges');
    }

    dispatchConfigurationError(message) {
      this.dispatchEvent(
        new CustomEvent('subscription-buybox:error', {
          bubbles: true,
          detail: { message, offerHandle: this.selectedOfferHandle },
        })
      );
    }

    getCheckedQuantity() {
      const checkedInput = this.querySelector('[data-quantity-button-input]:checked');
      const quantity = checkedInput ? parseInt(checkedInput.value, 10) : null;
      return Number.isFinite(quantity) ? quantity : null;
    }

    getCurrentQuantity() {
      const quantityInput = this.getQuantityInput();
      const quantity = quantityInput ? parseInt(quantityInput.value, 10) : null;
      return Number.isFinite(quantity) ? quantity : null;
    }

    getDefaultQuantity() {
      const quantity = this.getNumberAttribute(this, 'data-default-quantity', null);
      return Number.isFinite(quantity) ? quantity : null;
    }

    scheduleApplySelectedQuantity() {
      this.clearScheduledApply();

      [0, 80, 200].forEach((delay) => {
        const timer = window.setTimeout(() => {
          this.applySelectedQuantity();
        }, delay);

        this.reapplyTimers.push(timer);
      });
    }

    clearScheduledApply() {
      this.reapplyTimers.forEach((timer) => window.clearTimeout(timer));
      this.reapplyTimers = [];
    }

    applySelectedQuantity() {
      const quantityInput = this.getQuantityInput();
      const quantity = this.selectedQuantity || this.getCheckedQuantity();
      if (!quantityInput || !quantity) return;

      if (!this.isAllowedQuantity(quantity, quantityInput)) {
        this.syncState();
        return;
      }

      this.setQuantityValue(quantity);
      this.syncState();
    }

    setQuantityValue(quantity) {
      this.syncManagedInputState();

      this.collectQuantityInputs().forEach((input) => {
        const previousValue = input.value;
        input.value = quantity;

        if (previousValue !== input.value) {
          input.dispatchEvent(this.inputEvent);
          input.dispatchEvent(this.changeEvent);
        }
      });

      this.updateAddToCartPrice();
    }

    updateAddToCartPrice() {
      const skio = this.getSkioPlanPicker();
      skio?.updateAddToCartPrice?.();
      requestAnimationFrame(() => skio?.updateAddToCartPrice?.());
    }

    syncSellingPlanForQuantity(quantityButtonInput) {
      const skio = this.getSkioPlanPicker();
      const quantity = parseInt(quantityButtonInput.value, 10);
      const offer = this.getOfferByHandle(quantityButtonInput.dataset.offerHandle) || this.getOfferByQuantity(quantity);

      this.selectedOfferHandle = offer?.handle || null;

      const isMixMatchSelected = skio?.purchaseOption === 'mixmatch';
      if (!offer || (!this.hasActiveSellingPlan(skio) && !isMixMatchSelected)) return;

      const currentFrequency = this.getCurrentSellingPlanFrequency(skio);
      const frequency = this.defaultFrequencyQuantity === quantity ? currentFrequency : offer.defaultFrequency || currentFrequency;
      const sellingPlanId = this.getSellingPlanIdForQuantity(quantity, frequency, offer);

      if (!sellingPlanId) {
        this.selectedSellingPlanId = null;
        this.dispatchConfigurationError('No unambiguous selling plan matches this pack tier.');
        return;
      }

      this.defaultFrequencyQuantity = quantity;
      this.selectedSellingPlanId = sellingPlanId;
      this.syncSkioSellingPlansForCurrentVariant(skio);
      this.applySellingPlan(skio, sellingPlanId);
      this.scheduleApplySelectedSellingPlan();
    }

    getSkioPlanPicker() {
      const productInfo = this.closest('product-info');
      const scopedPicker = productInfo ? productInfo.querySelector('skio-plan-picker') : null;
      if (scopedPicker) return scopedPicker;

      return document.querySelector(`skio-plan-picker[formid="${this.dataset.formId}"], skio-plan-picker[formId="${this.dataset.formId}"]`);
    }

    getForm() {
      return this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    }

    getOneTimePurchaseButton() {
      return this.getForm()?.closest('product-form')?.querySelector('[data-one-time-purchase]') || null;
    }

    updateOneTimePurchasePrice() {
      const priceElement = this.getOneTimePurchaseButton()?.querySelector('[data-one-time-purchase-price]');
      const quantity = this.getCheckedQuantity() || this.getCurrentQuantity() || this.getDefaultQuantity();
      const variantId = this.getVariantInput()?.value;
      const variant = this.getVariantData().find((item) => String(item.id) === String(variantId));

      if (!priceElement || !quantity || !variant || !Number.isFinite(Number(variant.price))) return;

      const locale = document.documentElement.lang || navigator.language || 'en-US';
      const currency = window.Shopify?.currency?.active || 'USD';
      priceElement.textContent = new Intl.NumberFormat(locale, { style: 'currency', currency }).format((Number(variant.price) * quantity) / 100);
    }

    getSellingPlanIdForQuantity(quantity, frequency, suppliedOffer = null) {
      const offer = suppliedOffer || this.getOfferByQuantity(quantity);
      const frequencyKey = this.normalizeFrequencyKey(frequency) || this.normalizeFrequencyKey(offer?.defaultFrequency);
      if (!offer || !frequencyKey) return null;

      const matches = this.getSellingPlanData().filter((plan) => {
        const firstOrderAdjustment = (plan.priceAdjustments || []).find((adjustment) => {
          return Number(adjustment.orderCount) === 1 && String(adjustment.valueType || '').toLowerCase() === 'percentage';
        });

        return Number(firstOrderAdjustment?.value) === Number(offer.orderDiscount) && this.getPlanFrequency(plan) === frequencyKey;
      });

      const availableMatches = matches.filter((plan) => this.isSellingPlanAvailableForCurrentVariant(plan.id));
      return availableMatches.length === 1 ? String(availableMatches[0].id) : null;
    }

    getOffers() {
      if (this.offers) return this.offers;

      const script = this.querySelector('[data-quantity-buttons-offers-json]');
      try {
        this.offers = script ? JSON.parse(script.textContent) : [];
      } catch (error) {
        this.offers = [];
      }

      return this.offers;
    }

    getOfferByHandle(handle) {
      return this.getOffers().find((offer) => offer.handle === handle) || null;
    }

    getOfferByQuantity(quantity) {
      return this.getOffers().find((offer) => Number(offer.quantity) === Number(quantity)) || null;
    }

    getCheckedOfferHandle() {
      return this.querySelector('[data-quantity-button-input]:checked')?.dataset.offerHandle || null;
    }

    getSellingPlanData() {
      if (this.sellingPlanData) return this.sellingPlanData;

      const script = this.querySelector('[data-quantity-buttons-selling-plans-json]');

      try {
        this.sellingPlanData = script ? JSON.parse(script.textContent) : [];
      } catch (error) {
        this.sellingPlanData = [];
      }

      return this.sellingPlanData;
    }

    getPlanFrequency(plan) {
      const optionFrequency = (plan.options || []).map((option) => this.getFrequencyFromName(option)).find(Boolean);
      return optionFrequency || this.getFrequencyFromName(plan.name);
    }

    getVariantData() {
      if (this.variantData) return this.variantData;

      const script = this.querySelector('[data-quantity-buttons-variants-json]');
      this.variantData = script ? JSON.parse(script.textContent) : [];
      return this.variantData;
    }

    getVariantInput() {
      const form = document.getElementById(this.dataset.formId);
      return form ? form.querySelector('input[name="id"]') : null;
    }

    hasActiveSellingPlan(skio) {
      const sellingPlanInput = this.getSellingPlanInput();
      return Boolean(skio?.selectedSellingPlanGroup || skio?.selectedSellingPlan || (sellingPlanInput && !sellingPlanInput.disabled && sellingPlanInput.value));
    }

    getCurrentSellingPlanFrequency(skio) {
      const currentPlanId = String(skio?.selectedSellingPlan?.id || skio?.lastSubscriptionSellingPlan?.id || this.getSellingPlanInput()?.value || '');
      const currentFrequency = this.getFrequencyFromSellingPlanId(currentPlanId);

      if (currentFrequency) return currentFrequency;

      return this.getSkioFrequencyKey(skio, skio?.selectedSellingPlan) || this.getDefaultSellingPlanFrequency();
    }

    getFrequencyFromSellingPlanId(sellingPlanId) {
      const targetId = String(sellingPlanId || '');
      if (!targetId) return '';

      const sellingPlan = this.getSellingPlanData().find((plan) => String(plan.id) === targetId);
      return sellingPlan ? this.getFrequencyFromName(sellingPlan.name) : '';
    }

    getDefaultSellingPlanFrequency() {
      return String(this.getOfferByHandle(this.selectedOfferHandle)?.defaultFrequency || this.getOffers()[0]?.defaultFrequency || '');
    }

    normalizeFrequencyKey(frequency) {
      return this.getFrequencyFromName(frequency);
    }

    getFrequencyFromName(name) {
      const normalizedName = String(name || '').toLowerCase().replace(/\s+/g, ' ').trim();
      const deliveryInterval = normalizedName.match(/(?:every\s+)?(\d+)\s*days?\b/);
      if (deliveryInterval) return deliveryInterval[1];

      const matches = normalizedName.match(/\d+/g);
      return matches?.length ? matches[matches.length - 1] : '';
    }

    applySellingPlan(skio, sellingPlanId) {
      const match = skio ? this.findSellingPlanById(skio, sellingPlanId) : null;

      if (!match) return;

      this.refreshSkioSubscriptionBlock(skio, match);
      if (skio?.purchaseOption !== 'mixmatch') this.forceSellingPlanInputValue(sellingPlanId);
    }

    scheduleApplySelectedSellingPlan() {
      this.clearScheduledSellingPlanApply();

      [0, 50, 120, 250, 500].forEach((delay) => {
        const timer = window.setTimeout(() => {
          this.applySelectedSellingPlan();
        }, delay);

        this.sellingPlanReapplyTimers.push(timer);
      });
    }

    clearScheduledSellingPlanApply() {
      this.sellingPlanReapplyTimers.forEach((timer) => window.clearTimeout(timer));
      this.sellingPlanReapplyTimers = [];
    }

    applySelectedSellingPlan() {
      if (!this.selectedSellingPlanId) return;

      const skio = this.getSkioPlanPicker();
      this.syncSkioSellingPlansForCurrentVariant(skio);

      const match = skio ? this.findSellingPlanById(skio, this.selectedSellingPlanId) : null;

      if (!match || !this.isSellingPlanAvailableForCurrentVariant(this.selectedSellingPlanId)) return;

      this.refreshSkioSubscriptionBlock(skio, match);
      if (skio?.purchaseOption !== 'mixmatch') this.forceSellingPlanInputValue(this.selectedSellingPlanId);
      this.syncBadgeVisibility(match?.sellingPlan);
    }

    syncSkioSellingPlansForCurrentVariant(skio) {
      if (!skio || !this.selectedSellingPlanId) return;

      const currentVariantId = String(this.getVariantInput()?.value || '');
      const variant = skio.product?.variants?.find((item) => String(item.id) === currentVariantId);
      if (!variant) return;

      skio.selectedVariant = variant;

      if (typeof skio.getAvailableSellingPlanGroupsForVariant === 'function') {
        skio.availableSellingPlanGroups = skio.getAvailableSellingPlanGroupsForVariant(variant);
      }
    }

    refreshSkioSubscriptionBlock(skio, match) {
      if (!skio || !match?.sellingPlan) return;

      const preserveMixMatch = skio.purchaseOption === 'mixmatch';

      // Select the plan before rebuilding the available list. The Skio group
      // contains plans for all pack sizes, while the dropdown must show only
      // the delivery intervals for the pack the customer selected.
      skio.selectedSellingPlanGroup = match.group;
      skio.selectedSellingPlan = match.sellingPlan;
      if (typeof skio.getAvailableSellingPlanGroupsForVariant === 'function') {
        skio.availableSellingPlanGroups = skio.getAvailableSellingPlanGroupsForVariant(skio.selectedVariant);
      }

      const renderedMatch = this.findRenderedSkioSellingPlan(skio, match.sellingPlan.id) || match;
      renderedMatch.group.selected_selling_plan = renderedMatch.sellingPlan;
      skio.lastSubscriptionSellingPlan = renderedMatch.sellingPlan;
      if (preserveMixMatch) {
        skio.selectedSellingPlanGroup = null;
        skio.selectedSellingPlan = null;
        skio.purchaseOption = 'mixmatch';
      } else {
        skio.selectedSellingPlanGroup = renderedMatch.group;
        skio.selectedSellingPlan = renderedMatch.sellingPlan;
        skio.purchaseOption = 'subscription';
      }

      skio.updateForm?.();
      skio.syncExternalSellingPlanInput?.();
      skio.updateAddToCartPrice?.();
      skio.updateVariantPackBadgeVisibility?.();
      skio.requestUpdate?.('selectedSellingPlan');
      skio.requestUpdate?.('selectedSellingPlanGroup');
      skio.requestUpdate?.();

      skio.dispatchEvent(
        new CustomEvent('skio::update-selling-plan', {
          bubbles: true,
          composed: true,
          detail: {
            sellingPlan: renderedMatch.sellingPlan,
            key: skio.key,
          },
        })
      );

      if (!preserveMixMatch) {
        const updateInputs = () => this.updateSellingPlanInputs(renderedMatch.sellingPlan.id);
        requestAnimationFrame(updateInputs);
        skio.updateComplete?.then(updateInputs);
      }
    }

    findRenderedSkioSellingPlan(skio, sellingPlanId) {
      const targetId = String(sellingPlanId);

      for (const group of Array.isArray(skio.availableSellingPlanGroups) ? skio.availableSellingPlanGroups : []) {
        const sellingPlan = (group.selling_plans || []).find((plan) => String(plan.id) === targetId);
        if (sellingPlan) return { group, sellingPlan };
      }

      return null;
    }

    findSellingPlanById(skio, sellingPlanId) {
      const targetId = String(sellingPlanId);

      for (const group of this.getAllSkioSellingPlanGroups(skio)) {
        const sellingPlan = (group.selling_plans || []).find((plan) => String(plan.id) === targetId);
        if (sellingPlan && this.isSkioSellingPlanAvailable(skio, sellingPlan)) return { group, sellingPlan };
      }

      return null;
    }

    isSkioSellingPlanAvailable(skio, sellingPlan) {
      if (typeof skio?.isSellingPlanAvailableForVariant === 'function') {
        return skio.isSellingPlanAvailableForVariant(sellingPlan);
      }

      const availableIds = new Set((skio?.selectedVariant?.selling_plan_allocations || []).map((allocation) => String(allocation.selling_plan_id)));
      return availableIds.size === 0 || availableIds.has(String(sellingPlan?.id));
    }

    getAllSkioSellingPlanGroups(skio) {
      if (!skio) return [];

      const groups = [
        ...(Array.isArray(skio.availableSellingPlanGroups) ? skio.availableSellingPlanGroups : []),
        ...(Array.isArray(skio.skioSellingPlanGroups) ? skio.skioSellingPlanGroups : []),
        ...(Array.isArray(skio.product?.selling_plan_groups) ? skio.product.selling_plan_groups : []),
      ];

      return [...new Map(groups.filter(Boolean).map((group) => [group.id || group.name, group])).values()];
    }

    forceSellingPlanInputValue(sellingPlanId) {
      if (!sellingPlanId || !this.isSellingPlanAvailableForCurrentVariant(sellingPlanId)) return;

      this.updateSellingPlanInputs(sellingPlanId);
    }

    updateSellingPlanInputs(sellingPlanId) {
      this.getSellingPlanInputs().forEach((input) => {
        input.value = sellingPlanId;
        input.setAttribute('value', sellingPlanId);
        input.disabled = false;
        input.removeAttribute('disabled');
        input.dispatchEvent(this.inputEvent);
        input.dispatchEvent(this.changeEvent);
      });
    }

    getSellingPlanInputs() {
      const inputs = [];
      const form = this.getForm();

      if (form) {
        inputs.push(...form.querySelectorAll('input[name="selling_plan"]'));
      }

      if (window.CSS?.escape && this.dataset.formId) {
        inputs.push(...document.querySelectorAll(`input[name="selling_plan"][form="${CSS.escape(this.dataset.formId)}"]`));
      }

      const skio = this.getSkioPlanPicker();
      const skioRoot = skio?.renderRoot || skio?.shadowRoot;
      if (skioRoot) {
        inputs.push(...skioRoot.querySelectorAll('input[name="selling_plan"]'));
      }

      return [...new Set(inputs)];
    }

    isSellingPlanAvailableForCurrentVariant(sellingPlanId) {
      const currentVariantId = String(this.getVariantInput()?.value || '');
      if (!currentVariantId) return false;

      const currentVariant = this.getVariantData().find((variant) => String(variant.id) === currentVariantId);
      if (!currentVariant) return false;

      return (currentVariant.selling_plan_allocations || []).some((allocation) =>
        String(allocation.selling_plan_id || allocation.selling_plan?.id || '') === String(sellingPlanId)
      );
    }

    getSkioFrequencyKey(skio, sellingPlan) {
      if (typeof skio?.getSellingPlanFrequencyKey === 'function') {
        return skio.getSellingPlanFrequencyKey(sellingPlan);
      }

      return this.getFrequencyFromName(sellingPlan?.name);
    }

    isRelevantSellingPlanEvent(event) {
      const productInfo = this.closest('product-info');
      if (!productInfo || !event.target) return true;

      return productInfo.contains(event.target);
    }

    syncState() {
      const quantityInput = this.getQuantityInput();
      if (!quantityInput) return;

      const currentValue = parseInt(quantityInput.value, 10);

      this.querySelectorAll('[data-quantity-button-input]').forEach((input) => {
        const quantity = parseInt(input.value, 10);
        const allowed = this.isAllowedQuantity(quantity, quantityInput);

        input.disabled = !allowed;
        input.nextElementSibling?.toggleAttribute('aria-disabled', !allowed);
        input.checked = allowed && quantity === currentValue;
      });

      this.updateOneTimePurchasePrice();

    }

    isAllowedQuantity(quantity, input) {
      if (!Number.isFinite(quantity) || quantity < 1) return false;

      const min = this.getNumberAttribute(input, 'min', this.getNumberAttribute(input, 'data-min', 1));
      const max = this.getNumberAttribute(input, 'max', null);
      const step = this.getNumberAttribute(input, 'step', 1);

      if (quantity < min) return false;
      if (max !== null && quantity > max) return false;
      if (step > 1 && (quantity - min) % step !== 0) return false;

      return true;
    }

    getNumberAttribute(element, attribute, fallback) {
      const value = parseInt(element.getAttribute(attribute), 10);
      return Number.isFinite(value) ? value : fallback;
    }
  }

  customElements.define('product-quantity-buttons', ProductQuantityButtons);
})();
